import { useCallback } from 'react';

import { useContextMenu } from '@/components/context-menu/context-menu-context';
import { DropdownMenuItem } from '@/components/dropdown';
import { useExplorationCellContext } from '@/explore/exploration/exploration-cell-context';

import { useExplorationUIContext } from '../../../exploration/exploration-ui-context';

import { DataTableProperty } from '../types';
import { buildFilterMenuItem, buildGroupByMenuItem, buildHideColumnMenuItem } from './menu-items';

export interface OpenColumnContextMenuParams {
  position: {
    x: number;
    y: number;
  };
  property: DataTableProperty;
  canEditPipeline: boolean;
  hideColumn: (key: string) => void;
  isGroupedBy: boolean;
}

export const useColumnContextMenu = () => {
  const { openContextMenu } = useContextMenu();
  const { openEditor, openAddForm } = useExplorationUIContext();
  const { cell } = useExplorationCellContext();

  const buildMenu = useCallback(
    (
      property: DataTableProperty,
      canEditPipeline: boolean,
      hideColumn: (key: string) => void,
      isGroupedBy: boolean,
    ) => {
      const menuItems: DropdownMenuItem[] = [];

      if (canEditPipeline) {
        menuItems.push(buildFilterMenuItem(cell.id, openEditor, openAddForm, property));
        menuItems.push(buildGroupByMenuItem(cell.id, openEditor, openAddForm, property));
      }

      // Hiding columns is disabled for grouped columns and does not exist for SQL/Python blocks
      const hideColumnDisabled = isGroupedBy || cell.kind === 'sql' || cell.kind === 'python';

      menuItems.push(buildHideColumnMenuItem(property, hideColumn, hideColumnDisabled));

      return menuItems;
    },
    [openEditor, cell, openAddForm],
  );

  return (params: OpenColumnContextMenuParams) => {
    const { position, property, canEditPipeline, hideColumn, isGroupedBy } = params;
    openContextMenu({
      position,
      items: buildMenu(property, canEditPipeline, hideColumn, isGroupedBy),
    });
  };
};
